import React from 'react';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  return (
    <div className="bg-surface">
      {/* Header */}
      <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-display text-content sm:text-display-lg text-balance">
            About Clevor
          </h1>
          <p className="mt-6 text-lg leading-8 text-content-muted">
            We're building the platform we wished existed when we were bidding on government contracts.
          </p>
        </div>
      </div>

      {/* Story */}
      <div className="bg-surface-sunk py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-content-muted">
            <h2 className="text-title-lg text-content">The Problem We Lived</h2>
            <p>
              Government contracting is one of the largest markets in the world — over $700 billion
              in federal spending annually. But for small and mid-sized businesses, competing for
              those contracts is overwhelming. You're buried in solicitation documents, struggling
              to understand compliance requirements, spending weeks writing proposals, and managing
              everything in spreadsheets.
            </p>
            <p>
              The tools that exist were built for large primes with dedicated BD teams. They list
              contracts but don't help you understand them. They track pipelines but don't help
              you win.
            </p>

            <h2 className="text-title-lg text-content mt-12">What We're Building</h2>
            <p>
              Clevor is an AI-powered platform that doesn't just find opportunities — it reads them.
              When you save a solicitation document, our AI extracts the text, understands the
              requirements, and makes that knowledge available everywhere in the platform. Ask
              the AI Consultant a question and it answers from the actual RFP. Generate a proposal
              section and it's grounded in the real requirements. Run a compliance check and it
              tells you specifically what you need.
            </p>
            <p>
              Every feature connects to the next. Search leads to pipeline. Pipeline leads to
              documents. Documents feed the AI. AI powers proposals. It's one connected workflow
              from discovery to award.
            </p>

            <h2 className="text-title-lg text-content mt-12">Where We Are</h2>
            <p>
              Clevor is in active development. We're building in public and iterating fast based
              on real contractor feedback. The core platform is live — opportunity search across
              federal solicitations from SAM.gov, match scoring, document processing, an AI
              contracting consultant that reads your solicitation files, and a proposal writer
              that generates from actual RFP requirements.
            </p>
            <p>
              We're looking for early adopters who want to shape what this platform becomes.
              If you're a contractor tired of the status quo, we'd love to hear from you.
            </p>
          </div>

          <div className="mt-12 flex items-center gap-4">
            <Link to="/signup" className="btn-primary px-6 py-3">
              Get Started Free
            </Link>
            <a href="mailto:support@clevor.ai" className="text-sm font-semibold text-brand-600 hover:text-brand-700">
              Get in touch &rarr;
            </a>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-title-lg text-content text-center mb-12">How We Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 text-brand-700 font-bold mb-4">1</div>
              <h3 className="font-semibold text-content">Build What's Useful</h3>
              <p className="mt-2 text-sm text-content-muted">
                Every feature solves a real problem we experienced firsthand. No feature bloat, no vanity metrics.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 text-brand-700 font-bold mb-4">2</div>
              <h3 className="font-semibold text-content">AI With Context</h3>
              <p className="mt-2 text-sm text-content-muted">
                Generic AI is useless for GovCon. Our AI reads your actual documents and gives specific, grounded answers.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 text-brand-700 font-bold mb-4">3</div>
              <h3 className="font-semibold text-content">Transparent & Honest</h3>
              <p className="mt-2 text-sm text-content-muted">
                No fake metrics, no inflated claims. We show you what's real and let the product speak for itself.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
